import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { isEmpty } from 'lodash';

import styled from '@emotion/styled';

import { amplitudeService } from '../../../../shared/amplitude';
import { SelectCategorySort, SelectListProduct, SelectProductCart } from '../../../../shared/amplitude/events';
import { ProductSelectData } from '../../../../shared/interfaces/Product';

import ProductCard from '../components/ProductCard';
import Pagination from '../components/Pagination';
import Sort from '../components/Sort';
import { LoadingList, LoadingSearch } from '../components/LoadingList';

import COLOR from '../../../../shared/constant/colorset';
import { find48x48Cb5b5b5 } from '../../../../shared/images';

import { useAppSelector } from '../../../../shared/store';
import {
  pageTypeUpdate,
  productPageUpdate,
  productSortUpdate,
  unsetUpdate,
} from '../../slice';
import { productListSetScreenName } from '../../shared/util/productListSetScreenName';

const Products = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 1050px;
`;

const Title = styled.h3`
  padding: 15px 0 24px;
  font-weight: 500;
  font-size: 28px;
  line-height: 50px;
  letter-spacing: -0.5px;
  text-align: center;
`;

const Emph = styled.span`
  display: inline-block;
  overflow: hidden;
  max-width: 840px;
  color: ${COLOR.kurlyPurple};
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: top;
`;

const EmptyProduct = styled.div`
  padding: 100px 0;
  font-size: 16px;
  color: ${COLOR.kurlyGray400};
  text-align: center;
`;

const EmptySearch = styled(EmptyProduct)`
  padding: 222px 0 244px;
  :before {
    content: '';
    display: block;
    width: 48px;
    height: 48px;
    margin: 0 auto 16px;
    background: url(${find48x48Cb5b5b5}) no-repeat 50% 50%;
  }
`;

interface Props {
  perPage?: number;
  searchKeyword?: string;
}

export default function ListContainer({ perPage, searchKeyword }: Props) {
  const { hasSession } = useAppSelector(({ auth }) => auth);
  const [isSorting, setIsSorting] = useState(false);
  const {
    section,
    paramCode,
    sort,
    pagination,
    products,
    isLoading,
    queryId,
    perPage: listPerPage,
  } = useAppSelector(({ productList }) => productList);
  const { selectedType } = sort;
  const { currentPage, totalPages, totalProductsCount } = pagination;

  const dispatch = useDispatch();

  useEffect(() => {
    if (!paramCode || !section) {
      return;
    }
    productListSetScreenName(paramCode, section);
  }, [paramCode, section]);

  const onChangeSort = (value: { type: string; name: string; }) => {
    if (value.type === selectedType) {
      return;
    }

    dispatch(productSortUpdate(value));

    setIsSorting(true);

    amplitudeService.logEvent(new SelectCategorySort({
      section,
      queryId,
      value,
      totalProductsCount,
      paramCode,
    }));
  };

  const handleClickPage = (page: number) => {
    if (page === pagination.currentPage) {
      return;
    }

    dispatch(productPageUpdate(page));
  };

  const handleLinkClick = (selectLink: ProductSelectData) => {
    dispatch(unsetUpdate(true));

    amplitudeService.logEvent(new SelectListProduct({
      ...selectLink,
      section, paramCode, products, isSorting, perPage: listPerPage, pagination, sort, queryId,
    }));
  };

  const handleShortcut = (productShortcut: ProductSelectData) => {
    amplitudeService.logEvent(new SelectProductCart({
      ...productShortcut,
      section, paramCode, products, perPage: listPerPage, pagination, sort, isSorting, queryId,
    }));
  };

  useEffect(() => {
    if (!hasSession) {
      return;
    }

    if (!searchKeyword || !perPage) {
      return;
    }

    dispatch(pageTypeUpdate('search', searchKeyword, perPage));
  }, [dispatch, hasSession, perPage, searchKeyword]);

  if (isEmpty(products) && isLoading) {
    return (
      <>
        {section === 'search'
          ? <LoadingSearch />
          : <LoadingList />
        }
      </>
    );
  }

  if (isEmpty(products)) {
    return (
      <>
        {section === 'search'
          ? <>
            <Title>
              {'\''}
              <Emph>{paramCode}</Emph>
              {'\''}에 대한 검색결과
            </Title>
            <EmptySearch>검색된 상품이 없습니다.</EmptySearch>
          </>
          : <EmptyProduct>등록된 상품이 없습니다.</EmptyProduct>
        }
      </>
    );
  }

  return (
    <>
      {section === 'search' &&
        <Title>
          {'\''}
          <Emph>{paramCode}</Emph>
          {'\''}에 대한 검색결과
        </Title>
      }
      <Sort
        onChangeSort={onChangeSort}
      />
      <Products>
        {products.map(({
          no, name, salesPrice, discount, listImageUrl, groupProduct,
          sticker, canRestockNotify, status,
          tags, shortDescription, deliveryTypeNames, isMultiplePrice, isBuyNow,
        }, index) => (
          <ProductCard
            key={no}
            index={index}
            name={name}
            productNo={no}
            price={salesPrice}
            discount={discount}
            imageUrl={listImageUrl}
            tags={tags}
            shortDescription={shortDescription}
            deliveryTypeNames={deliveryTypeNames}
            sticker={sticker ? sticker : undefined}
            status={status}
            isGroupProduct={groupProduct.isGroup}
            canRestockNotify={canRestockNotify}
            isMultiplePrice={isMultiplePrice}
            isBuyNow={isBuyNow}
            queryId={queryId}
            handleLinkClick={handleLinkClick}
            selectProduct={handleShortcut}
          />
        ))}
      </Products>
      {
        pagination?.totalPages > 1
        && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onClickPage={handleClickPage}
          />
        )
      }
    </>
  );
}
